import { useQueryParams } from "@certn/api";
import {
  GhostButton,
  PrimaryButton,
  SecondaryTopNavWithStepper,
} from "@certn/cascadia-react";
import { t } from "i18next";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";

import { useUser } from "../../../hooks/useUser";
import { OrderContext, useOrderContext } from "../OrderContext";
import { clearOrderDraft, useOrderDraftKey } from "../orderDraftStorage";
import { useOrderBillingProfile } from "../steps/useOrderBillingProfile";

interface Props {
  onComplete?: () => void;
  isDisabled?: boolean;
  submitting?: boolean;
}

export function OrderStepHeader({ onComplete, isDisabled, submitting }: Props) {
  const navigate = useNavigate();
  const { blockOrder, isQuickscreen } = useOrderContext();
  const { order, goToPrevStep, currentStep, isLoading } =
    useContext(OrderContext);
  const { params } = useQueryParams();
  const { details: userDetails } = useUser();
  const orderDraftKey = useOrderDraftKey();

  const { data } = useOrderBillingProfile();

  const cancel = () => {
    clearOrderDraft(orderDraftKey);
    navigate("/");
  };

  const steps = isQuickscreen
    ? [t("Order selection"), t("Review")]
    : [
        params.package_id ? t("Package details") : t("Order selection"),
        t("Order details"),
        t("Review"),
      ];

  const atLastStep = currentStep >= steps.length - 1;

  return (
    <SecondaryTopNavWithStepper
      testID="order-page-top-nav"
      stepperSteps={steps.map((step, index) => ({
        title: step,
        status: index < currentStep ? "COMPLETE" : "INCOMPLETE",
      }))}
      handlePrevStep={goToPrevStep}
      handleClose={cancel}
      currentStepIndex={currentStep}
    >
      {data?.invoicing_frequency !== "DAILY" && (
        <GhostButton onPress={cancel} testID="order-page-top-nav-cancel-button">
          {t("Cancel")}
        </GhostButton>
      )}

      {!atLastStep && (
        <PrimaryButton
          onPress={onComplete}
          isDisabled={isDisabled || submitting || isLoading || blockOrder}
          isLoading={submitting}
          testID="order-page-top-nav-next-step-button"
        >
          {t("Next step")}
        </PrimaryButton>
      )}

      {atLastStep &&
        !isQuickscreen &&
        (data?.invoicing_frequency !== "DAILY" ||
          data?.payer_option === "APPLICANT") && (
          <PrimaryButton
            onPress={onComplete}
            isDisabled={
              submitting ||
              isDisabled ||
              userDetails?.is_proxy_user ||
              isLoading
            }
            isLoading={submitting}
            testID="order-page-top-nav-send-order-button"
          >
            {t("Send ({{number}})", { number: order.recipients.length })}
          </PrimaryButton>
        )}
    </SecondaryTopNavWithStepper>
  );
}
